import { FC } from 'react';
import styled from 'styled-components';
import type { ChannelSlice } from '../forecasting.types';
import { formatCurrency, formatPercent } from '../utils/forecastingAggregations';
import { ChartTitle, Panel } from '../Forecasting.styles';
import { FORECASTING_THEME } from '../forecastingTheme';

interface ChannelMixListProps {
  /** Channels with their share, largest first. */
  data: ChannelSlice[];
  metricLabel: string;
}

const List = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 7px;
`;

const Item = styled.li`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  row-gap: 3px;
  column-gap: 12px;
  font-size: 12px;
`;

const Name = styled.span`
  color: ${FORECASTING_THEME.dataText};
  font-weight: 400;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Share = styled.span`
  color: ${FORECASTING_THEME.text};
  font-weight: 600;
  font-variant-numeric: tabular-nums;
`;

/* A thin bar under each channel, so relative size reads at a glance without a pie. */
const Track = styled.span`
  grid-column: 1 / -1;
  height: 3px;
  border-radius: 2px;
  background: ${FORECASTING_THEME.hoverBg};
  overflow: hidden;
`;

const Fill = styled.span<{ $percent: number }>`
  display: block;
  height: 100%;
  width: ${({ $percent }) => Math.max(0, Math.min(100, $percent))}%;
  background: ${FORECASTING_THEME.accent};
  border-radius: 2px;
`;

/** A share that rounds to 0% but is not zero reads as "<1%", not as nothing. */
const formatShare = (percent: number): string =>
  percent > 0 && percent < 0.5 ? '<1%' : formatPercent(percent, 0);

const Empty = styled.p`
  margin: 0;
  font-size: 12px;
  color: ${FORECASTING_THEME.textMuted};
`;

/**
 * Channel mix as a ranked list of channels and their share of the total.
 *
 * Replaces the pie chart on the annual view: a list takes a narrow column, so
 * the monthly bar chart beside it gets the width, and shares are read as
 * numbers rather than estimated from slice sizes. Hovering a channel shows its
 * amount.
 */
export const ChannelMixList: FC<ChannelMixListProps> = ({ data, metricLabel }) => (
  <Panel>
    <ChartTitle>Channel mix — {metricLabel}</ChartTitle>
    {data.length === 0 ? (
      <Empty>No channels for these filters.</Empty>
    ) : (
      <List>
        {data.map((channel) => (
          <Item key={channel.name} title={`${channel.name}: ${formatCurrency(channel.value)}`}>
            <Name>{channel.name}</Name>
            <Share>{formatShare(channel.percent)}</Share>
            <Track aria-hidden>
              <Fill $percent={channel.percent} />
            </Track>
          </Item>
        ))}
      </List>
    )}
  </Panel>
);
